import React, { useEffect, useState } from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material/styles';
import {
    AppBar,
    Badge,
    Box,
    IconButton,
    Toolbar,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';

import { useBreakpoint } from 'common/hooks';
import searchBar from 'common/modules/serchBar';

import HelpMenu from './HelpMenu';
import Logo from './Logo';
import ProfileMenu from './ProfileMenu';

const sxBadge = {
    '& .MuiBadge-badge': {
        backgroundColor: (theme) => theme.palette.primary.light,
        color: (theme) => theme.palette.primary.contrastText,
    },
};

const TopBar = ({
    handleDrawerToggle,
    match,
    profileValues,
    redirect,
    searchFieldRef,
    selectedTheme,
    setTheme,
}) => {
    const theme = useTheme();
    const breakpoint = useBreakpoint();
    const isDarkMode = theme.palette.mode === 'dark';
    const [helpAnchorEl, setHelpAnchorEl] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);

    const handleHelpClick = (event) => {
        setHelpAnchorEl(event.currentTarget);
    };
    const handleHelpClose = () => {
        setHelpAnchorEl(null);
    };

    useEffect(() => {
        if (!window.Intercom) return;

        let timeoutId;

        window.Intercom('onUnreadCountChange', (count) => {
            setUnreadCount(count);
            if (count > 0) {
                window.Intercom('hideNotifications', false);

                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                timeoutId = setTimeout(() => {
                    window.Intercom('hideNotifications', true);
                }, 5000);
            }
        });

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    return (
        <AppBar
            position={['xs', 'sm'].includes(breakpoint) ? 'fixed' : 'static'}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: 'rgba(242, 244, 249, 1)',
                boxShadow: isDarkMode ? 'none' : null,
                color: '#ffffff',
                height: '65px',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <Toolbar>
                    <IconButton
                        onClick={handleDrawerToggle}
                        sx={{
                            display: { xs: 'block', sm: 'block', md: 'none' },
                        }}
                    >
                        <Logo svgColor="white" />
                    </IconButton>
                </Toolbar>
                <searchBar.Container searchFieldRef={searchFieldRef} />
            </Box>

            <Box
                sx={{
                    textAlign: 'center',
                    mt: 0,
                    mr: 2,
                    display: 'flex',
                    gap: 1,
                }}
            >
                <IconButton
                    id="help-menu-button"
                    aria-label="Ayuda"
                    size="small"
                    onClick={handleHelpClick}
                    sx={{
                        color: 'primary.main',
                    }}
                >
                    <Badge badgeContent={unreadCount} sx={sxBadge}>
                        <HelpOutlineIcon sx={{ fontSize: 24 }} />
                    </Badge>
                </IconButton>

                <HelpMenu
                    anchorEl={helpAnchorEl}
                    handleClose={handleHelpClose}
                    intercomNotifications={unreadCount}
                    sxBadge={sxBadge}
                />

                <ProfileMenu
                    match={match}
                    profileValues={profileValues}
                    redirect={redirect}
                    selectedTheme={selectedTheme}
                    setTheme={setTheme}
                />
            </Box>
        </AppBar>
    );
};

TopBar.propTypes = {
    handleDrawerToggle: PropTypes.func,
    match: PropTypes.object,
    profileValues: PropTypes.object,
    redirect: PropTypes.func,
    searchFieldRef: PropTypes.object,
    selectedTheme: PropTypes.string,
    setTheme: PropTypes.func,
};

export default TopBar;
